import { CookieOptions, StorageOptions } from './types';
import { LocalStorageProvider } from './local-storage';
import { SessionStorageProvider } from './session-storage';
import { CookieStorageProvider } from './cookie-storage';
import { SecureWorkerProvider } from './secure-worker';

/**
 * Configuration for PersistStore
 */
interface PersistStoreConfig {
  /**
   * Enable logging of all storage operations
   */
  enableLogging?: boolean;
}

/**
 * Storage registry entry
 */
interface StorageRegistryEntry {
  key: string;
  storageType: string;
  persistOnLogout: boolean;
}

/**
 * PersistStore - A utility for managing persistent data across different storage mechanisms
 */
class PersistStore {
  private static instance: PersistStore;
  
  private localStorageProvider: LocalStorageProvider;
  private sessionStorageProvider: SessionStorageProvider;
  private cookieStorageProvider: CookieStorageProvider;
  private secureWorkerProvider: SecureWorkerProvider | null = null;
  private config: PersistStoreConfig = { enableLogging: false };
  
  // Registry to track which items should persist across logout
  private storageRegistry: Map<string, StorageRegistryEntry> = new Map();
  
  // Registry key in localStorage to persist the registry itself
  private readonly REGISTRY_KEY = '__persiststore_registry';

  private constructor() {
    this.localStorageProvider = new LocalStorageProvider();
    this.sessionStorageProvider = new SessionStorageProvider();
    this.cookieStorageProvider = new CookieStorageProvider();
    
    // Initialize registry from localStorage
    this.initRegistry();
  }
  
  /**
   * Initialize the storage registry from localStorage
   */
  private async initRegistry(): Promise<void> {
    try {
      const serializedRegistry = await this.localStorageProvider.get<string>(this.REGISTRY_KEY);
      if (serializedRegistry) {
        const parsedRegistry: Array<StorageRegistryEntry> = JSON.parse(serializedRegistry);
        parsedRegistry.forEach(entry => {
          this.storageRegistry.set(`${entry.storageType}:${entry.key}`, entry);
        });
        this.logOperation('INIT_REGISTRY', 'registry', 'loaded', `${this.storageRegistry.size} entries`);
      }
    } catch (error) {
      console.error('Failed to initialize storage registry:', error);
      // Create a new registry if loading fails
      this.storageRegistry = new Map();
    }
  }
  
  /**
   * Save the registry to localStorage
   */
  private async saveRegistry(): Promise<void> {
    try {
      const serializedRegistry = JSON.stringify(Array.from(this.storageRegistry.values()));
      await this.localStorageProvider.put(this.REGISTRY_KEY, serializedRegistry);
      this.logOperation('SAVE_REGISTRY', 'registry', 'saved', `${this.storageRegistry.size} entries`);
    } catch (error) {
      console.error('Failed to save storage registry:', error);
    }
  }
  
  /**
   * Register an item in the storage registry
   * @param key The key to register
   * @param storageType The storage type
   * @param persistOnLogout Whether the item should persist across logout
   */
  private async registerItem(key: string, storageType: string, persistOnLogout: boolean): Promise<void> {
    const registryKey = `${storageType}:${key}`;
    const entry: StorageRegistryEntry = { key, storageType, persistOnLogout };
    
    // Only update if the value changed
    const existingEntry = this.storageRegistry.get(registryKey);
    if (!existingEntry || existingEntry.persistOnLogout !== persistOnLogout) {
      this.storageRegistry.set(registryKey, entry);
      await this.saveRegistry();
    }
  }

  /**
   * Get the singleton instance of PersistStore
   */
  public static getInstance(): PersistStore {
    if (!PersistStore.instance) {
      PersistStore.instance = new PersistStore();
    }
    return PersistStore.instance;
  }

  /**
   * Configure the PersistStore
   * @param config Configuration options
   */
  public configure(config: PersistStoreConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Initialize the secure worker provider
   * @param workerUrl URL to the secure worker script
   */
  public initSecureWorker(workerUrl: string = '/secure-worker.js'): void {
    if (!this.secureWorkerProvider) {
      this.secureWorkerProvider = new SecureWorkerProvider(workerUrl);
    }
  }

  /**
   * Log a storage operation if logging is enabled
   */
  private logOperation(operation: string, storageType: string, key: string, value?: any): void {
    if (this.config.enableLogging) {
      const valueStr = value ? (typeof value === 'object' ? '(object)' : `${value}`) : '';
      console.debug(`[PersistStore] ${operation} | ${storageType} | ${key} | ${valueStr}`);
    }
  }

  /**
   * Get a value from local storage
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  public async getLocal<T>(key: string): Promise<T | null> {
    const result = await this.localStorageProvider.get<T>(key);
    this.logOperation('GET', this.localStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Store a value in local storage
   * @param key The key to store the value under
   * @param value The value to store
   * @param options Storage options
   */
  public async putLocal<T>(key: string, value: T, options?: StorageOptions): Promise<void> {
    const persistOnLogout = options?.persistOnLogout ?? false;
    this.logOperation('PUT', this.localStorageProvider.getStorageType(), key, value);
    
    // Register the item with its persistence preference
    await this.registerItem(key, this.localStorageProvider.getStorageType(), persistOnLogout);
    
    return this.localStorageProvider.put<T>(key, value);
  }

  /**
   * Remove a value from local storage
   * @param key The key to remove
   */
  public async removeLocal(key: string): Promise<void> {
    this.logOperation('REMOVE', this.localStorageProvider.getStorageType(), key);
    return this.localStorageProvider.remove(key);
  }

  /**
   * Check if a key exists in local storage
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  public async hasLocal(key: string): Promise<boolean> {
    const result = await this.localStorageProvider.has(key);
    this.logOperation('HAS', this.localStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Get a value from session storage
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  public async getSession<T>(key: string): Promise<T | null> {
    const result = await this.sessionStorageProvider.get<T>(key);
    this.logOperation('GET', this.sessionStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Store a value in session storage
   * @param key The key to store the value under
   * @param value The value to store
   * @param options Storage options
   */
  public async putSession<T>(key: string, value: T, options?: StorageOptions): Promise<void> {
    const persistOnLogout = options?.persistOnLogout ?? false;
    this.logOperation('PUT', this.sessionStorageProvider.getStorageType(), key, value);
    
    // Register the item with its persistence preference
    await this.registerItem(key, this.sessionStorageProvider.getStorageType(), persistOnLogout);
    
    return this.sessionStorageProvider.put<T>(key, value);
  }

  /**
   * Remove a value from session storage
   * @param key The key to remove
   */
  public async removeSession(key: string): Promise<void> {
    this.logOperation('REMOVE', this.sessionStorageProvider.getStorageType(), key);
    return this.sessionStorageProvider.remove(key);
  }

  /**
   * Check if a key exists in session storage
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   */
  public async hasSession(key: string): Promise<boolean> {
    const result = await this.sessionStorageProvider.has(key);
    this.logOperation('HAS', this.sessionStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Get a value from cookies
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  public async getCookie<T>(key: string): Promise<T | null> {
    const result = await this.cookieStorageProvider.get<T>(key);
    this.logOperation('GET', this.cookieStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Store a value in cookies
   * @param key The key to store the value under
   * @param value The value to store
   * @param options Cookie options
   */
  public async putCookie<T>(key: string, value: T, options?: CookieOptions): Promise<void> {
    const persistOnLogout = options?.persistOnLogout ?? false;
    this.logOperation('PUT', this.cookieStorageProvider.getStorageType(), key, value);
    
    // Register the item with its persistence preference
    await this.registerItem(key, this.cookieStorageProvider.getStorageType(), persistOnLogout);
    
    return this.cookieStorageProvider.put<T>(key, value, options);
  }

  /**
   * Remove a cookie
   * @param key The key to remove
   * @param options Cookie options (path and domain must match the cookie to be removed)
   */
  public async removeCookie(key: string, options?: Pick<CookieOptions, 'path' | 'domain'>): Promise<void> {
    this.logOperation('REMOVE', this.cookieStorageProvider.getStorageType(), key);
    return this.cookieStorageProvider.remove(key, options);
  }

  /**
   * Check if a cookie exists
   * @param key The key to check
   * @returns True if the cookie exists, false otherwise
   */
  public async hasCookie(key: string): Promise<boolean> {
    const result = await this.cookieStorageProvider.has(key);
    this.logOperation('HAS', this.cookieStorageProvider.getStorageType(), key);
    return result;
  }

  /**
   * Get a value from secure storage (requires initSecureWorker to be called first)
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   * @throws Error if secure worker is not initialized
   */
  public async getSecure<T>(key: string): Promise<T | null> {
    this.ensureSecureWorkerInitialized();
    const result = await this.secureWorkerProvider!.get<T>(key);
    this.logOperation('GET', this.secureWorkerProvider!.getStorageType(), key);
    return result;
  }

  /**
   * Store a value in secure storage (requires initSecureWorker to be called first)
   * @param key The key to store the value under
   * @param value The value to store
   * @param options Storage options
   * @throws Error if secure worker is not initialized
   */
  public async putSecure<T>(key: string, value: T, options?: StorageOptions): Promise<void> {
    this.ensureSecureWorkerInitialized();
    const persistOnLogout = options?.persistOnLogout ?? false;
    this.logOperation('PUT', this.secureWorkerProvider!.getStorageType(), key, value);
    
    // Register the item with its persistence preference
    await this.registerItem(key, this.secureWorkerProvider!.getStorageType(), persistOnLogout);
    
    return this.secureWorkerProvider!.put<T>(key, value);
  }

  /**
   * Remove a value from secure storage (requires initSecureWorker to be called first)
   * @param key The key to remove
   * @throws Error if secure worker is not initialized
   */
  public async removeSecure(key: string): Promise<void> {
    this.ensureSecureWorkerInitialized();
    this.logOperation('REMOVE', this.secureWorkerProvider!.getStorageType(), key);
    return this.secureWorkerProvider!.remove(key);
  }

  /**
   * Check if a key exists in secure storage (requires initSecureWorker to be called first)
   * @param key The key to check
   * @returns True if the key exists, false otherwise
   * @throws Error if secure worker is not initialized
   */
  public async hasSecure(key: string): Promise<boolean> {
    this.ensureSecureWorkerInitialized();
    const result = await this.secureWorkerProvider!.has(key);
    this.logOperation('HAS', this.secureWorkerProvider!.getStorageType(), key);
    return result;
  }

  /**
   * Clear all non-persistent items on logout
   * This method should be called during the logout process
   */
  public async clearOnLogout(): Promise<void> {
    try {
      this.logOperation('CLEAR_ON_LOGOUT', 'all-storage', 'Started');
      
      // Loop through registry and remove non-persistent items
      for (const [key, entry] of this.storageRegistry.entries()) {
        if (!entry.persistOnLogout) {
          // Remove the item from the appropriate storage
          switch (entry.storageType) {
            case this.localStorageProvider.getStorageType():
              await this.removeLocal(entry.key);
              break;
              
            case this.sessionStorageProvider.getStorageType():
              await this.removeSession(entry.key);
              break;
              
            case this.cookieStorageProvider.getStorageType():
              await this.removeCookie(entry.key);
              break;
              
            case this.secureWorkerProvider?.getStorageType():
              if (this.secureWorkerProvider) {
                await this.removeSecure(entry.key);
              }
              break;
              
            default:
              console.warn(`Unknown storage type: ${entry.storageType} for key: ${entry.key}`);
          }
          
          // Remove the item from the registry
          this.storageRegistry.delete(key);
        }
      }
      
      // Save the updated registry
      await this.saveRegistry();
      
      this.logOperation('CLEAR_ON_LOGOUT', 'all-storage', 'Completed');
    } catch (error) {
      console.error('Error during clearOnLogout:', error);
      throw error;
    }
  }
  
  /**
   * Cleanup resources when the store is no longer needed
   */
  public cleanup(): void {
    if (this.secureWorkerProvider) {
      this.secureWorkerProvider.terminate();
      this.secureWorkerProvider = null;
    }
  }

  /**
   * Ensure the secure worker is initialized
   * @throws Error if secure worker is not initialized
   */
  private ensureSecureWorkerInitialized(): void {
    if (!this.secureWorkerProvider) {
      throw new Error('Secure worker not initialized. Call initSecureWorker() first.');
    }
  }
}

// Export a singleton instance
export const persistStore = PersistStore.getInstance();

// Export types and providers for advanced usage
export type { CookieOptions } from './types';
export { LocalStorageProvider } from './local-storage';
export { SessionStorageProvider } from './session-storage';
export { CookieStorageProvider } from './cookie-storage';
export { SecureWorkerProvider } from './secure-worker';